<template>
  <form @submit.prevent="onSubmit" class="flight-form" novalidate>
    <!-- Tipo de vuelo -->
    <div class="flight-type">
      <label
        ><input type="radio" value="roundtrip" v-model="tripType" /> Ida y
        regreso</label
      >
      <label
        ><input type="radio" value="oneway" v-model="tripType" /> Solo
        ida</label
      >
      <label>
        <input type="radio" value="multicity" v-model="tripType" /> Multidestino
      </label>
    </div>

    <div class="boxSearchContainer">
      <div class="boxSearch">
        <!-- Origen y destino con autocomplete -->
        <div class="inputs-row-labeled">
          <div class="icon-input-wrapper autocomplete-wrapper">
            <img :src="positionIcon" alt="position icon" class="input-icon" />
            <div class="labeled-input">
              <label for="departure-input">Origen</label>
              <input
                id="departure-input"
                type="text"
                v-model="departureInput"
                @input="searchCity('departure', departureInput)"
                placeholder="Ciudad Origen"
                required
                autocomplete="off"
                :class="{ 'is-invalid': validationErrors.departure }"
              />
              <div v-if="isLoadingDeparture" class="loading-spinner-overlay"></div>
              <ul
                v-if="sortedDepartureSuggestions.length"
                class="autocomplete-list"
              >
                <template
                  v-for="(city, index) in sortedDepartureSuggestions"
                  :key="index"
                >
                  <li
                    v-if="
                      shouldShowTitle(sortedDepartureSuggestions, index, true)
                    "
                    class="autocomplete-title"
                  >
                    Ciudades
                  </li>
                  <li
                    v-if="
                      shouldShowTitle(sortedDepartureSuggestions, index, false)
                    "
                    class="autocomplete-title"
                  >
                    Aeropuertos
                  </li>
                  <li
                    @click="selectCity('departure', city)"
                    class="autocomplete-item"
                  >
                    <div class="location-details">
                      <span class="location-name"
                        >{{ city.Name }} ({{ city.Code }})</span
                      >
                      <span class="location-country">{{
                        city.CountryName
                      }}</span>
                    </div>
                  </li>
                </template>
              </ul>
            </div>
          </div>

          <div class="icon-input-wrapper autocomplete-wrapper">
            <img :src="positionIcon" alt="position icon" class="input-icon" />
            <div class="labeled-input">
              <label for="arrival-input">Destino</label>
              <input
                id="arrival-input"
                type="text"
                v-model="arrivalInput"
                @input="searchCity('arrival', arrivalInput)"
                placeholder="Ciudad Destino"
                required
                autocomplete="off"
                :class="{ 'is-invalid': validationErrors.arrival }"
              />
              <div v-if="isLoadingArrival" class="loading-spinner-overlay"></div>
              <ul
                v-if="sortedArrivalSuggestions.length"
                class="autocomplete-list"
              >
                <template
                  v-for="(city, index) in sortedArrivalSuggestions"
                  :key="index"
                >
                  <li
                    v-if="
                      shouldShowTitle(sortedArrivalSuggestions, index, true)
                    "
                    class="autocomplete-title"
                  >
                    Ciudades
                  </li>
                  <li
                    v-if="
                      shouldShowTitle(sortedArrivalSuggestions, index, false)
                    "
                    class="autocomplete-title"
                  >
                    Aeropuertos
                  </li>
                  <li
                    @click="selectCity('arrival', city)"
                    class="autocomplete-item"
                  >
                    <div class="location-details">
                      <span class="location-name"
                        >{{ city.Name }} ({{ city.Code }})</span
                      >
                      <span class="location-country">{{
                        city.CountryName
                      }}</span>
                    </div>
                  </li>
                </template>
              </ul>
            </div>
          </div>
        </div>

        <!-- Fechas -->
        <div class="icon-input-wrapper">
          <img :src="calendarIcon" alt="calendar icon" class="input-icon" />
          <div class="labeled-input">
            <label for="departure-date">Salida</label>
            <flat-pickr
              id="departure-date"
              v-model="departureDate"
              :config="departureFlatpickrConfig"
              placeholder="Selecciona fecha"
              required
              :class="{ 'is-invalid': validationErrors.departureDate }"
              @on-change="onDepartureChange"
            />
          </div>
        </div>
        <div class="icon-input-wrapper" v-if="tripType === 'roundtrip'">
          <img :src="calendarIcon" alt="calendar icon" class="input-icon" />
          <div class="labeled-input">
            <label for="return-date">Regreso</label>
            <flat-pickr
              id="return-date"
              ref="returnDateInput"
              v-model="returnDate"
              :config="returnFlatpickrConfig"
              placeholder="Selecciona fecha"
              :class="{ 'is-invalid': validationErrors.returnDate }"
            />
          </div>
        </div>

        <!-- Pasajeros -->
        <div class="icon-input-wrapper passenger-wrapper">
          <img :src="userIcon" alt="user icon" class="input-icon" />
          <div class="labeled-input">
            <label>Pasajeros y Clase</label>
            <div class="passenger-selector" @click="togglePassengerModal">
              <span
                >{{ totalPassengers }} pasajero(s),
                {{ flightClassDisplay }}</span
              >
            </div>
          </div>

          <div class="passenger-modal" v-if="showPassengerModal" @click.stop>
            <div class="passenger-row">
              <span class="passenger-label">Adultos</span>
              <div class="passenger-controls">
                <button type="button" @click="updatePassengers('adults', -1)">
                  -
                </button>
                <span class="passenger-count">{{ passengers.adults }}</span>
                <button type="button" @click="updatePassengers('adults', 1)">
                  +
                </button>
              </div>
            </div>
            <div class="passenger-row">
              <div class="passenger-label-group">
                <span class="passenger-label">Niños</span>
                <span class="passenger-sublabel">2 a 12 años</span>
              </div>
              <div class="passenger-controls">
                <button type="button" @click="updatePassengers('children', -1)">
                  -
                </button>
                <span class="passenger-count">{{ passengers.children }}</span>
                <button type="button" @click="updatePassengers('children', 1)">
                  +
                </button>
              </div>
            </div>

            <!-- Edades de los niños -->
            <div v-if="passengers.children > 0" class="child-ages">
              <p class="child-age-notice">
                Por favor, tenga en cuenta que la edad ingresada del niño debe
                ser al momento del ultimo vuelo.
              </p>
              <div
                v-for="(age, index) in childAges"
                :key="'child-age-' + index"
                class="inputs-row"
              >
                <label style="margin-right: 10px"
                  >Edad niño {{ index + 1 }}:</label
                >
                <div class="custom-select-wrapper child-age-select">
                  <select v-model="childAges[index]" :style="{ backgroundImage: `url(${iconToggle})` }">
                    <option v-for="n in 10" :key="n + 1" :value="n + 1">
                      {{ n + 1 }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="passenger-row">
              <div class="passenger-label-group">
                <span class="passenger-label">Infantes</span>
                <span class="passenger-sublabel">(0 - 23 meses)</span>
              </div>
              <div class="passenger-controls">
                <button type="button" @click="updatePassengers('infants', -1)">
                  -
                </button>
                <span class="passenger-count">{{ passengers.infants }}</span>
                <button type="button" @click="updatePassengers('infants', 1)">
                  +
                </button>
              </div>
            </div>

            <div class="passenger-row">
              <span class="passenger-label">Clase</span>
              <div class="custom-select-wrapper">
                <select v-model="flightClass" :style="{ backgroundImage: `url(${iconToggle})` }">
                  <option value="economic">Económica</option>
                  <option value="executive">Ejecutiva</option>
                  <option value="firstclass">Primera clase</option>
                </select>
              </div>
            </div>

            <div class="passenger-close">
              <button type="button" @click="togglePassengerModal">
                Cerrar
              </button>
            </div>
          </div>
        </div>

        <!-- Botón Agregar Trayecto (Solo visible en Multidestino) -->
        <div v-if="tripType === 'multicity'" class="add-segment-btn-inline" @click="addSegment">
          <span>+ Agregar trayecto</span>
        </div>
      </div>

      <!-- Botón -->
      <button type="submit">
        Buscar
        <img :src="planeIcon" alt="Buscar" class="search-btn-icon" />
      </button>
    </div>

    <!-- Opciones de equipaje -->
    <div class="inputs-row" style="margin-top: 15px">
      <label
        ><input type="radio" value="baggageall" v-model="baggageOption" /> Todas
        las tarifas</label
      >
      <label
        ><input type="radio" value="baggageinhold" v-model="baggageOption" />
        Equipaje en bodega</label
      >
      <label
        ><input type="radio" value="baggageincabin" v-model="baggageOption" />
        Equipaje de cabina</label
      >
      <label
        ><input type="radio" value="baggageinhand" v-model="baggageOption" />
        Equipaje de mano</label
      >
    </div>

    <!-- Trayectos adicionales Multidestino -->
    <div v-if="tripType === 'multicity' && additionalSegments.length > 0" class="multicity-segments-area" ref="multicityArea">
      <div v-for="(segment, index) in additionalSegments" :key="index" class="segment-extra-row">
        <div class="segment-inputs-grid">
          <!-- Origen Trayecto Adicional -->
          <div class="icon-input-wrapper autocomplete-wrapper">
            <img :src="positionIcon" alt="position icon" class="input-icon" />
            <div class="labeled-input">
              <label>Origen</label>
              <input
                type="text"
                v-model="segment.departureInput"
                @input="searchCity('departure', segment.departureInput, index)"
                placeholder="Ciudad Origen"
                autocomplete="off"
                :class="{ 'is-invalid': validationErrors.segments?.[index]?.departure }"
              />
              <div v-if="segment.isLoadingDeparture" class="loading-spinner-overlay"></div>
              <ul v-if="segment.departureSuggestions.length" class="autocomplete-list">
                <template v-for="(city, sIndex) in segment.departureSuggestions" :key="sIndex">
                  <li v-if="shouldShowTitle(segment.departureSuggestions, sIndex, true)" class="autocomplete-title">Ciudades</li>
                  <li v-if="shouldShowTitle(segment.departureSuggestions, sIndex, false)" class="autocomplete-title">Aeropuertos</li>
                  <li @click="selectCity('departure', city, index)" class="autocomplete-item">
                    <div class="location-details">
                      <span class="location-name">{{ city.Name }} ({{ city.Code }})</span>
                      <span class="location-country">{{ city.CountryName }}</span>
                    </div>
                  </li>
                </template>
              </ul>
            </div>
          </div>

          <!-- Destino Trayecto Adicional -->
          <div class="icon-input-wrapper autocomplete-wrapper">
            <img :src="positionIcon" alt="position icon" class="input-icon" />
            <div class="labeled-input">
              <label>Destino</label>
              <input
                type="text"
                v-model="segment.arrivalInput"
                @input="searchCity('arrival', segment.arrivalInput, index)"
                placeholder="Ciudad Destino"
                autocomplete="off"
                :class="{ 'is-invalid': validationErrors.segments?.[index]?.arrival }"
              />
              <div v-if="segment.isLoadingArrival" class="loading-spinner-overlay"></div>
              <ul v-if="segment.arrivalSuggestions.length" class="autocomplete-list">
                <template v-for="(city, sIndex) in segment.arrivalSuggestions" :key="sIndex">
                  <li v-if="shouldShowTitle(segment.arrivalSuggestions, sIndex, true)" class="autocomplete-title">Ciudades</li>
                  <li v-if="shouldShowTitle(segment.arrivalSuggestions, sIndex, false)" class="autocomplete-title">Aeropuertos</li>
                  <li @click="selectCity('arrival', city, index)" class="autocomplete-item">
                    <div class="location-details">
                      <span class="location-name">{{ city.Name }} ({{ city.Code }})</span>
                      <span class="location-country">{{ city.CountryName }}</span>
                    </div>
                  </li>
                </template>
              </ul>
            </div>
          </div>

          <!-- Fecha Salida Trayecto Adicional -->
          <div class="icon-input-wrapper">
            <img :src="calendarIcon" alt="calendar icon" class="input-icon" />
            <div class="labeled-input">
              <label>Salida</label>
              <flat-pickr
                v-model="segment.departureDate"
                :config="getSegmentFlatpickrConfig(index)"
                placeholder="Selecciona fecha"
                :class="{ 'is-invalid': validationErrors.segments?.[index]?.departureDate }"
                @on-change="(selectedDates, dateStr) => onSegmentDateChange(index, dateStr)"
              />
            </div>
          </div>

          <!-- Botón Eliminar (Icono) -->
          <div class="remove-segment-container">
            <button type="button" class="remove-segment-btn-icon" @click="removeSegment(index)" title="Eliminar trayecto">
              <img :src="trashIcon" alt="Eliminar" class="trash-icon" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import axios from "axios";
import { flightStore } from "@/stores/flightstore.js";
import positionIcon from "@/assets/icons/position.svg";
import calendarIcon from "@/assets/icons/icon-calendar.svg";
import userIcon from "@/assets/icons/icon-user.svg";
import planeIcon from "@/assets/images/plane-white-last.svg";
import trashIcon from "@/assets/icons/trash.svg";
import iconToggle from "@/assets/icons/icon-toggle.svg";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { Spanish } from "flatpickr/dist/l10n/es.js";

export default {
  name: "FlightSearchForm",
  components: {
    flatPickr,
  },
  props: {
    initialPayload: {
      type: Object,
      default: null,
    },
  },
  data() {
    const daysToAdd =
      parseInt(process.env.VUE_APP_MAX_CALENDAR_PURCHASE, 10) || 0;
    // Se calcula la fecha de inicio por defecto una sola vez
    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() + daysToAdd);
    defaultStartDate.setMinutes(
      defaultStartDate.getMinutes() - defaultStartDate.getTimezoneOffset(),
    );
    const defaultStartDateStr = defaultStartDate.toISOString().slice(0, 10);

    // Usamos la misma fecha (string) para minDate para evitar problemas con la hora actual
    const minDateValue = defaultStartDateStr;

    return {
      positionIcon,
      calendarIcon,
      userIcon,
      planeIcon,
      trashIcon,
      iconToggle,      
      departureFlatpickrConfig: {
        locale: Spanish, // Usamos el idioma español
        dateFormat: "Y-m-d", // Formato que se guarda en el v-model
        altInput: true, // Muestra un formato amigable al usuario
        altFormat: "d M Y", // Formato amigable
        disableMobile: true, // Fuerza el calendario de escritorio en móviles
        minDate: minDateValue, // No permite seleccionar fechas pasadas
      },
      returnFlatpickrConfig: {
        locale: Spanish,
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "d M Y",
        disableMobile: true,
        minDate: minDateValue, // Inicializamos con la misma restricción
      },
      tripType: "roundtrip",
      departureCode: "",
      arrivalCode: "",
      departureInput: "",
      arrivalInput: "",
      departureSuggestions: [],
      arrivalSuggestions: [],
      departureDate: defaultStartDateStr,
      returnDate: defaultStartDateStr,
      additionalSegments: [], // Array para trayectos multidestino
      passengers: {
        adults: 1,
        children: 0,
        infants: 0,
      },
      childAges: [],
      flightClass: "economic",
      showPassengerModal: false,
      baggageOption: "baggageall",
      isCityDeparture: false,
      isCityArrival: false,
      debounceTimer: null,
      isLoadingDeparture: false,
      isLoadingArrival: false,
      lastDepartureRequestId: 0,
      lastArrivalRequestId: 0,
      validationErrors: {}, // Objeto para almacenar errores de validación
      isLoaded: false,
    };
  },
  computed: {
    sortedDepartureSuggestions() {
      return [...this.departureSuggestions].sort((a, b) => {
        if (a.IsCity === b.IsCity) return 0;
        return a.IsCity ? -1 : 1;
      });
    },
    sortedArrivalSuggestions() {
      return [...this.arrivalSuggestions].sort((a, b) => {
        if (a.IsCity === b.IsCity) return 0;
        return a.IsCity ? -1 : 1;
      });
    },
    totalPassengers() {
      const { adults, children, infants } = this.passengers;
      return adults + children + infants;
    },
    flightClassDisplay() {
      const classMap = {
        economic: "Económica",
        executive: "Ejecutiva",
        firstclass: "Primera clase",
      };
      return classMap[this.flightClass] || "Económica";
    },
  },
  watch: {
    departureDate(newDate) {
      // Actualiza la fecha mínima permitida en el calendario de regreso
      this.returnFlatpickrConfig = {
        ...this.returnFlatpickrConfig,
        minDate: newDate,
      };
      // Si la fecha de regreso actual es anterior a la nueva fecha de salida, la ajustamos
      if (this.returnDate && newDate > this.returnDate) {
        this.returnDate = newDate;
      }
    },
    // Watchers para limpiar errores de validación en tiempo real
    departureInput(newValue) {
      if (newValue && this.validationErrors.departure) {
        delete this.validationErrors.departure;
      }
    },
    arrivalInput(newValue) {
      if (newValue && this.validationErrors.arrival) {
        delete this.validationErrors.arrival;
      }
    },
    returnDate(newValue) {
      if (newValue && this.validationErrors.returnDate) {
        delete this.validationErrors.returnDate;
      }
    },
  },
  mounted() {
    // Si recibimos datos iniciales, los usamos para rellenar el formulario
    if (this.initialPayload) {
      this.tripType = this.initialPayload.tripType;
      this.departureCode = this.initialPayload.departureCode;
      this.arrivalCode = this.initialPayload.arrivalCode;
      this.departureInput = this.initialPayload.departureInput;
      this.arrivalInput = this.initialPayload.arrivalInput;
      this.departureDate = this.initialPayload.departureDate;
      this.returnDate = this.initialPayload.returnDate;
      // Usamos spread (...) para crear copias y evitar mutaciones directas de la prop
      this.passengers = { ...this.initialPayload.passengers };
      this.childAges = [...this.initialPayload.childAges];
      this.flightClass = this.initialPayload.flightClass;
      this.baggageOption = this.initialPayload.baggageOption;
      this.isCityDeparture = this.initialPayload.isCityDeparture;
      this.isCityArrival = this.initialPayload.isCityArrival;
      if (this.tripType === 'multicity' && this.initialPayload.multiCitySearchRequests) {
        // Reconstruimos los trayectos adicionales (desde el segundo elemento en adelante)
        this.additionalSegments = this.initialPayload.multiCitySearchRequests.slice(1).map(s => ({
          departureCode: s.departureCode,
          arrivalCode: s.arrivalCode,
          departureInput: s.departureInput || "", // Persistimos los inputs para la UI
          arrivalInput: s.arrivalInput || "",
          departureDate: s.departureDate,
          departureSuggestions: [],
          arrivalSuggestions: [],
          isLoadingDeparture: false,
          isLoadingArrival: false,
          lastDepartureRequestId: 0,
          lastArrivalRequestId: 0,
          isCityDeparture: s.isCityDeparture,
          isCityArrival: s.isCityArrival
        }));
      }
    }
    // Evita que el calendario se abra automáticamente al cargar la página
    this.$nextTick(() => {
      this.isLoaded = true;
    });
  },
  methods: {
    onSubmit() {
      // Primero, validamos el formulario. Si no es válido, detenemos la ejecución.
      if (!this.validateForm()) {
        return;
      }

      const payload = {
        tripType: this.tripType,
        departureCode: this.departureCode,
        arrivalCode: this.arrivalCode,
        departureInput: this.departureInput,
        arrivalInput: this.arrivalInput,
        departureDate: this.departureDate,
        returnDate: this.tripType === "roundtrip" ? this.returnDate : null,
        passengers: this.passengers,
        childAges: this.childAges,
        flightClass: this.flightClass,
        baggageOption: this.baggageOption,
        isCityDeparture: this.isCityDeparture,
        isCityArrival: this.isCityArrival,
        // Adaptamos a la estructura MultiCitySearchRequests de la API
        multiCitySearchRequests: this.tripType === 'multicity' ? [
          {
            departureCode: this.departureCode,
            arrivalCode: this.arrivalCode,
            departureDate: this.departureDate,
            isCityDeparture: this.isCityDeparture,
            isCityArrival: this.isCityArrival,
            departureInput: this.departureInput, // Mantenemos inputs para persistencia local
            arrivalInput: this.arrivalInput
          },
          ...this.additionalSegments.map(s => ({
            departureCode: s.departureCode,
            arrivalCode: s.arrivalCode,
            departureDate: s.departureDate,
            isCityDeparture: s.isCityDeparture,
            isCityArrival: s.isCityArrival,
            departureInput: s.departureInput,
            arrivalInput: s.arrivalInput
          }))
        ] : null
      };

      // Guardamos el payload en el store para que la vista de resultados lo pueda leer
      flightStore.searchPayload = payload;

      this.$emit("search", payload);
      this.showPassengerModal = false;
    },
    onDepartureChange(selectedDates, dateStr) {
      if (this.isLoaded && this.tripType === "roundtrip" && dateStr) {
        this.$nextTick(() => {
          const returnPicker = this.$refs.returnDateInput;
          if (returnPicker && returnPicker.fp) {
            returnPicker.fp.open();
          }
        });
      }
    },
    updatePassengers(type, delta) {
      const min = type === "adults" ? 1 : 0;
      this.passengers[type] = Math.max(min, this.passengers[type] + delta);

      if (type === "children") {
        const current = this.childAges.length;
        const target = this.passengers.children;
        if (target > current) {
          this.childAges.push(...Array(target - current).fill(2));
        } else if (target < current) {
          this.childAges.splice(target);
        }
      }
    },
    togglePassengerModal() {
      this.showPassengerModal = !this.showPassengerModal;
    },
    addSegment() {
      const daysToAdd = parseInt(process.env.VUE_APP_MAX_CALENDAR_PURCHASE, 10) || 0;
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + daysToAdd);
      const dateStr = defaultDate.toISOString().slice(0, 10);

      this.additionalSegments.push({
        departureCode: "",
        arrivalCode: "",
        departureInput: "",
        arrivalInput: "",
        departureDate: dateStr,
        departureSuggestions: [],
        arrivalSuggestions: [],
        isLoadingDeparture: false,
        isLoadingArrival: false,
        lastDepartureRequestId: 0,
        lastArrivalRequestId: 0,
        isCityDeparture: false,
        isCityArrival: false
      });

      // En la siguiente actualización del DOM, hacemos scroll hacia el área de trayectos
      this.$nextTick(() => {
        const area = this.$refs.multicityArea;
        if (area) {
          area.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    },
    removeSegment(index) {
      this.additionalSegments.splice(index, 1);
    },
    getSegmentFlatpickrConfig(index) {
      // La fecha mínima de un segmento debería ser la fecha del segmento anterior (o del principal si es el primero extra)
      const prevDate = index === 0 ? this.departureDate : this.additionalSegments[index - 1].departureDate;
      return {
        ...this.departureFlatpickrConfig,
        minDate: prevDate
      };
    },
    searchCity(type, value, index = null) {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(async () => {
        this.clearSuggestions(type, index);

        if (!value || value.length < 2) {
          return;
        }

        const isDeparture = type === "departure";
        const target = index === null ? this : this.additionalSegments[index];
        
        const requestIdProp = isDeparture ? "lastDepartureRequestId" : "lastArrivalRequestId";
        const loadingProp = isDeparture ? "isLoadingDeparture" : "isLoadingArrival";
        const suggestionsProp = isDeparture ? "departureSuggestions" : "arrivalSuggestions";

        const requestId = ++target[requestIdProp];
        target[loadingProp] = true;

        try {
          const response = await axios.get(
            `${process.env.VUE_APP_WEBAPIURL}City/GetCities?letters=${encodeURIComponent(value)}`,
          );

          if (requestId === target[requestIdProp]) {
            const results = Object.values(response.data || {});
            if (index !== null) {
              results.sort((a, b) => (a.IsCity === b.IsCity ? 0 : a.IsCity ? -1 : 1));
            }
            target[suggestionsProp] = results;
          }
        } catch (error) {
          console.error("Error fetching cities:", error);
        } finally {
          if (requestId === target[requestIdProp]) {
            target[loadingProp] = false;
          }
        }
      }, 300);
    },
    onSegmentDateChange(index, dateStr) {
      if (dateStr && this.validationErrors.segments?.[index]) {
        delete this.validationErrors.segments[index].departureDate;
        if (Object.keys(this.validationErrors.segments[index]).length === 0) {
          delete this.validationErrors.segments[index];
        }
        if (Object.keys(this.validationErrors.segments).length === 0) {
          delete this.validationErrors.segments;
        }
      }
    },
    selectCity(type, city, index = null) {
      const display = `${city.Name}, ${city.CountryName} (${city.Code})`;
      const target = index === null ? this : this.additionalSegments[index];

      if (type === "departure") {
        target.departureCode = city.Code;
        target.departureInput = display;
        target.isCityDeparture = city.IsCity;
        target.departureSuggestions = [];
      } else {
        target.arrivalCode = city.Code;
        target.arrivalInput = display;
        target.isCityArrival = city.IsCity;
        target.arrivalSuggestions = [];
      }
    },
    clearSuggestions(type, index = null) {
      const target = index === null ? this : this.additionalSegments[index];
      if (type === "departure") target.departureSuggestions = [];
      else target.arrivalSuggestions = [];
    },
    shouldShowTitle(list, index, isCity) {
      if (list.length === 0) return false;
      const current = list[index];
      if (current.IsCity !== isCity) return false;
      if (index === 0) return true;
      const prev = list[index - 1];
      return prev.IsCity !== current.IsCity;
    },
    validateForm() {
      const errors = {};

      // Usamos .trim() para asegurarnos de que no se envíen solo espacios en blanco.
      if (!this.departureCode || !this.departureInput.trim()) {
        errors.departure = true;
      }
      if (!this.arrivalCode || !this.arrivalInput.trim()) {
        errors.arrival = true;
      }
      if (!this.departureDate) {
        errors.departureDate = true;
      }
      if (this.tripType === "roundtrip" && !this.returnDate) {
        errors.returnDate = true;
      }

      // Validación de trayectos adicionales (Multidestino)
      if (this.tripType === 'multicity' && this.additionalSegments.length > 0) {
        this.additionalSegments.forEach((segment, index) => {
          if (!segment.departureCode || !segment.departureInput.trim()) {
            if (!errors.segments) errors.segments = {};
            if (!errors.segments[index]) errors.segments[index] = {};
            errors.segments[index].departure = true;
          }
          if (!segment.arrivalCode || !segment.arrivalInput.trim()) {
            if (!errors.segments) errors.segments = {};
            if (!errors.segments[index]) errors.segments[index] = {};
            errors.segments[index].arrival = true;
          }
          if (!segment.departureDate) {
            if (!errors.segments) errors.segments = {};
            if (!errors.segments[index]) errors.segments[index] = {};
            errors.segments[index].departureDate = true;
          }
        });
      }

      this.validationErrors = errors;

      // Si el objeto de errores está vacío, el formulario es válido.
      return Object.keys(errors).length === 0;
    },
  },
};
</script>

<style scoped>
.flight-form {
  max-width: 90%;
  margin: auto;
}

/* Nuevo contenedor para campos con etiqueta */
.inputs-row-labeled {
  display: flex;
  flex: 0.2; /* Ajustamos el espacio para origen/destino */
  gap: 10px;
  align-items: stretch; /* Hacemos que los hijos ocupen toda la altura */
}

.icon-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px; /* Espacio entre el icono y el bloque de input */
  position: relative; /* Para el autocomplete */
}

.labeled-input {
  display: flex;
  flex-direction: column;
  justify-content: center; /* Centra verticalmente la etiqueta y el input */
  flex: 1;
  position: relative; /* Para el autocomplete */
}

.input-icon {
  width: 20px;
  height: 20px;
}

.labeled-input label {
  font-size: 0.8rem; /* Aumentamos un poco el tamaño */
  color: var(--color-acento-secundario); /* Color solicitado */
  font-weight: 400; /* Grosor de fuente normal */
  margin-bottom: 4px;
}

.inputs-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.inputs-row input[type="text"],
.labeled-input input[type="text"] {
  padding: 0; /* Quitamos padding para que no sume a la altura */
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.9rem; /* Reducimos un poco la fuente del input */
  height: auto; /* La altura la controla el contenedor */
  color: inherit;
}

/* Estilo para campos inválidos con línea inferior */
.is-invalid {
  border-bottom: 1px solid #ad26263b !important;
}

/* Estilo específico para el input de flatpickr cuando es inválido */
.labeled-input :deep(.is-invalid.form-control) {
  border-bottom: 1px solid #ad26263b !important;
}
/* Estilo para los placeholders y el texto de fecha por defecto */
.inputs-row input::placeholder,
.labeled-input input::placeholder {
  color: inherit; /* Hereda el color */
  opacity: 0.7; /* Un poco más claro para diferenciarlo del texto ingresado */
  font-size: 0.9rem;
}
.autocomplete-wrapper {
}
.autocomplete-list {
  position: absolute;
  top: 100%;
  left: -30px; /* Desplaza la lista a la izquierda para alinearla con el icono */
  right: 0;
  background: white;
  border: 1px solid #ccc;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  border-radius: 5px;
  min-width: 300px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 0;
  margin-top: 4px;
}
.autocomplete-title {
  padding: 8px 12px;
  font-size: 0.75rem;
  font-weight: bold;
  color: #6c757d;
  background-color: #f8f9fa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e9ecef;
  border-top: 1px solid #e9ecef;
  cursor: default;
}
.autocomplete-title:first-child {
  border-top: none;
}
.autocomplete-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}
.autocomplete-item:hover {
  background-color: #f0f0f0;
}
.location-details {
  display: flex;
  flex-direction: column;
}
.location-name {
  font-weight: 600;
}
.location-country {
  font-size: 0.8rem;
  color: #555;
}
.boxSearch > .icon-input-wrapper {
  flex: 0.5; /* Reducimos un poco más el flex-grow */
  max-width: 150px; /* Forzamos un ancho máximo para los campos de fecha */
}

.boxSearch > .icon-input-wrapper.passenger-wrapper {
  flex: 1.5; /* Aumentamos el espacio para la sección de pasajeros */
  max-width: none; /* Quitamos el ancho máximo para que pueda crecer */
}

.flight-type {
  margin-bottom: 15px;
}
.flight-type label {
  margin-right: 20px;
  color: white;
}
.flight-form > .inputs-row label {
  color: white;
}
.passenger-wrapper {
  position: relative;
}
.passenger-selector {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.9rem;
  display: flex;
  height: 100%;
  align-items: center;
  cursor: pointer;
}
.passenger-modal {
  position: absolute;
  background: white;
  border: none;
  border-radius: 16px;
  min-width: 320px;
  width: auto;
  z-index: 1000;
  padding: 20px;
  top: calc(100% + 15px);
  right: 0;
  left: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  color: var(--color-acento-secundario);
}
.passenger-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 8px 0;
}
.passenger-label {
  flex-grow: 1; /* Ocupa el espacio disponible, empujando los controles */
}

.passenger-label-group {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.passenger-sublabel {
  font-size: 0.75rem;
  color: var(--color-acento-secundario-suave); /* Un tono más claro del azul corporativo */
  margin-top: 2px;
}

.passenger-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.passenger-count {
  min-width: 20px;
  text-align: center;
  font-weight: bold;
  font-size: 1rem;
}

.passenger-row button {
  width: 32px;
  height: 32px;
  font-size: 18px;
  border: 1px solid var(--color-acento-primario);
  background: white;
  color: var(--color-acento-primario);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  line-height: 1;
}
.passenger-row button:hover {
  background-color: var(--color-acento-primario);
  color: white;
}
.passenger-close {
  text-align: center; /* Centramos el contenido */
  margin-top: 10px;
}

.child-ages {
  padding: 10px 0;
}

.child-age-notice {
  text-align: center;
  font-size: 0.8rem;
  color: #555;
  margin-bottom: 15px;
}
.passenger-close button {
  background-color: var(--color-acento-primario);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 30px;
  width: 100%;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.passenger-close button:hover {
  background-color: var(--color-acento-primario-suave);
}

button[type="submit"] {
  background-color: var(--color-acento-primario); /* Color azul solicitado */
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 50px; /* Bordes redondeados para forma de píldora */
  cursor: pointer;
  font-family: inherit;
  font-weight: bold;
  white-space: nowrap;
  height: 60px; /* Mantenemos altura para alinear con la barra */
  font-size: 0.9rem;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 10px rgba(0, 189, 214, 0.3); /* Sombra sutil a juego */
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
button[type="submit"]:hover {
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.25),
    0 2px 5px rgba(0, 189, 214, 0.2);
  transform: translateY(1px); /* Ligero desplazamiento hacia abajo */
}
.search-btn-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}
.boxSearch {
  background: rgba(255, 255, 255, 0.8);
  height: 60px;
  border-radius: 30px;
  display: flex;
  flex-grow: 1;
  align-items: center;
  text-align: left;
  gap: 10px;
  padding: 0 15px;
}
.boxSearchContainer {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Sobrescribimos estilos de flatpickr para que se integre */
.labeled-input :deep(.form-control) {
  padding: 0;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.9rem;
  height: auto;
  color: var(--color-acento-secundario); /* Color de texto corporativo */
  font-family: inherit;
  box-shadow: none; /* Quitamos cualquier sombra que pueda tener */
  width: 82px; /* Ancho fijo para los campos de fecha */
}
/* --- Estilos para Selects Personalizados --- */
.custom-select-wrapper {
  position: relative;
  display: inline-block;
  width: 150px; /* Ancho base, se puede ajustar */
}

/* Ancho específico para el select de edad de los niños */
.child-age-select {
  width: 80px;
}

.custom-select-wrapper select {
  /* Apariencia personalizada */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  /* Estilos de caja y borde */
  width: 100%;
  padding: 8px 32px 8px 12px;
  border: 1px solid #b0c4de;
  border-radius: 8px;
  background-color: white;

  /* Flecha personalizada */
  background-repeat: no-repeat;
  background-position: right 10px center;

  /* Tipografía */
  font-family: inherit;
  font-size: 0.9rem;
  color: var(--color-acento-secundario);
  cursor: pointer;
}

/* --- Responsive Design --- */
@media (max-width: 1024px) {
  .flight-form {
    max-width: 100%;
    padding: 0 16px; /* Añadimos padding lateral en móvil */
  }

  .boxSearchContainer {
    flex-direction: column; /* Apila la barra de búsqueda y el botón */
    gap: 20px;
  }

  .boxSearch {
    flex-direction: column; /* Apila los campos del buscador */
    height: auto; /* Altura automática para acomodar los campos */
    border-radius: 0;
    padding: 0;
    gap: 10px;
    width: 100%;
    background: transparent; /* Fondo transparente para que cada item tenga su propio fondo */
  }

  .inputs-row-labeled {
    flex-direction: column; /* Apila Origen y Destino */
    width: 100%;
    flex: 1; /* Reseteamos el flex-grow */
    gap: 10px; /* Espacio entre los items internos */
  }

  /* Reseteamos flex para que no colapse la altura en móvil */
  .boxSearch > .icon-input-wrapper,
  .boxSearch > .icon-input-wrapper.passenger-wrapper {
    width: 100%;
    max-width: none; /* Quitamos el ancho máximo en móvil */
    flex: 0 0 auto; /* flex-grow: 0, flex-shrink: 0, flex-basis: auto (respeta height) */
  }

  /* Estilos para convertir cada campo en una burbuja independiente en móvil */
  .boxSearch > .icon-input-wrapper,
  .inputs-row-labeled > .icon-input-wrapper {
    background: rgba(255, 255, 255, 0.8);
    border-radius: 30px; /* Figura ovalada alargada */
    padding: 0 20px; /* Padding lateral interno */
    height: 60px; /* Altura fija para la forma */
    display: flex;
    align-items: center;
  }

  button[type="submit"] {
    width: 100%;
    height: 50px;
  }

  .passenger-modal {
    left: 0;
    right: auto;
    width: 100%;
    min-width: 0;
  }

  /* Centrar selectores de tipo de vuelo y equipaje en móvil */
  .flight-type {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
  }
  .flight-type label {
    margin: 0 10px; /* Distribuye el margen uniformemente para centrar */
  }
  .flight-form > .inputs-row {
    justify-content: center;
  }

  .multicity-segments-area {
    position: relative;
    top: 0;
    left: 0;
    transform: none;
    width: 100%;
    max-width: none;
    padding: 0;
    background: transparent;
    box-shadow: none;
    border: none;
    margin-top: 15px;
    max-height: none;
    overflow-y: visible;
  }

  .segment-extra-row {
    background: rgba(255, 255, 255, 0.1); /* Fondo sutil para agrupar tramos en móvil */
    padding: 12px;
    border-radius: 20px;
    margin-bottom: 15px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .segment-inputs-grid {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }

  .segment-inputs-grid .icon-input-wrapper,
  .segment-inputs-grid .icon-input-wrapper:nth-child(3) {
    flex: none !important;
    width: 100% !important;
    min-width: 0 !important;
    height: 60px; /* Misma altura que el buscador principal */
    background: rgba(255, 255, 255, 0.85) !important;
    border-radius: 30px !important;
    border: none !important;
    padding: 0 20px !important;
  }

  .remove-segment-container {
    width: 100%;
    justify-content: flex-end;
    padding-top: 5px;
  }
}

/* --- ESTILOS PERSONALIZADOS PARA FLATPICKR --- */
/* 
  Estos estilos se aplican de forma global porque flatpickr
  crea el calendario al final del <body>, fuera del alcance
  de los estilos 'scoped' del componente.
*/
:global(.flatpickr-calendar) {
  background: #f4f7f9;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  border: none;
}

:global(.flatpickr-months .flatpickr-month) {
  background: var(--color-acento-terciario);
  color: white;
}

:global(.flatpickr-months .flatpickr-prev-month),
:global(.flatpickr-months .flatpickr-next-month) {
  fill: white;
  color: white;
}

:global(.flatpickr-weekdays) {
  background:var(--color-acento-terciario);
}

:global(span.flatpickr-weekday) {
  color: white;
  font-weight: 500;
}

:global(.flatpickr-day.selected),
:global(.flatpickr-day.startRange),
:global(.flatpickr-day.endRange) {
  background: var(--color-acento-terciario);
  border-color: var(--color-acento-terciario);
}

:global(.flatpickr-day.today) {
  border-color: var(--color-acento-primario); /* Borde con el color de acento */
}

:global(.flatpickr-day:hover) {
  background: #eef2f7;
}

/* Estilos para el spinner de carga */
.loading-spinner-overlay {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid var(--color-acento-primario);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  z-index: 101;
}

/* --- Estilos Multidestino --- */
.add-segment-btn-inline {
  margin-left: auto;
  margin-right: 10px;
  cursor: pointer;
  padding: 8px 15px;
  border-radius: 20px;
  background: rgba(0, 189, 214, 0.1);
  color: var(--color-acento-primario);
  font-weight: 600;
  font-size: 0.8rem;
  white-space: nowrap;
  transition: all 0.2s ease;
  border: 1px dashed var(--color-acento-primario);
}

.add-segment-btn-inline:hover {
  background: rgba(0, 189, 214, 0.2);
  transform: scale(1.02);
}

.multicity-segments-area {
  position: absolute;
  top: 100%; /* Aparece justo después de las opciones de equipaje */
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 980px; /* Ancho más equilibrado para desktop */
  z-index: 1000;
  padding: 12px;
  /* Efecto de cajón blanco limpio */
  background: #ffffff;
  border-radius: 15px;
  border: 1px solid #e9ecef;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: none; 
  overflow: visible; /* Asegura que los autocompletados no se corten */
  padding-bottom: 20px;
}

.segment-extra-row {
  position: relative;
  background: #f8f9fa; /* Gris humo muy ligero */
  border-bottom: 1px solid #eef2f7;
  padding: 8px 12px;
  border-radius: 15px;
  animation: fadeInDown 0.3s ease-out;
}

.segment-extra-row:focus-within {
  z-index: 10; /* Eleva la fila actual sobre las siguientes para ver sugerencias */
}

.remove-segment-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
}

.remove-segment-btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.remove-segment-btn-icon:hover {
  background-color: #ffeef0;
}

.trash-icon {
  width: 18px;
  height: 18px;
  opacity: 0.5;
  transition: opacity 0.2s;
}

.remove-segment-btn-icon:hover .trash-icon {
  opacity: 1;
}

.segment-inputs-grid {
  display: flex;
  gap: 8px; /* Gap más compacto */
  flex-wrap: wrap;
  align-items: center;
}

.segment-inputs-grid .icon-input-wrapper {
  background: white;
  border-radius: 15px;
  padding: 2px 12px;
  height: 48px;
  flex: 2.5; /* Damos un poco más de peso relativo a ciudades vs fecha */
  min-width: 150px;
  border: 1px solid #edf2f7;
}

/* Ajuste específico para que el campo de fecha sea más pequeño en la grid */
.segment-inputs-grid .icon-input-wrapper:nth-child(3) {
  flex: 1; /* La fecha ocupa la mitad que los campos de ciudad */
  min-width: 120px;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
